import { useNavigate, useLocation } from "react-router-dom";
import { Box, CircularProgress, Button, Avatar } from "@mui/material";
import { useEffect } from "react";
import { useGetSubAdmin } from "../../api/hooks/useSubAdmin";
import { showError } from "../../helpers/toast";
import type { SubAdmin } from "../../api/types/subAdmin.types";

const SubadminDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const subAdminId = location?.state?.subAdminId;

    // Fetch sub-admin data
    const { data: subAdminData, isLoading: isLoadingSubAdmin, error: subAdminError } = useGetSubAdmin(
        subAdminId || '',
        { enabled: !!subAdminId }
    );

    const subAdmin: SubAdmin | null = subAdminData?.data || null;

    useEffect(() => {
        if (subAdminError) {
            showError((subAdminError as any)?.response?.data?.message || "Failed to load sub-admin details");
        }
    }, [subAdminError]);

    useEffect(() => {
        if (!subAdminId && !isLoadingSubAdmin) {
            showError("No sub-admin selected. Redirecting to sub-admin list.");
            navigate("/manage-subadmin");
        }
    }, [subAdminId, isLoadingSubAdmin, navigate]);

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });
        } catch {
            return "N/A";
        }
    };

    if (isLoadingSubAdmin) {
        return (
            <>
                <div className="main_title">
                    <h1>Sub-Admin Detail</h1>
                    <p>
                        <button onClick={() => navigate("/dashboard")}>Dashboard</button>
                        {' '} - <button onClick={() => navigate("/manage-subadmin")}>Manage Sub-Admin</button>
                        {' '} - Sub-Admin Detail
                    </p>
                </div>
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                    <CircularProgress />
                </Box>
            </>
        );
    }

    if (!subAdmin) {
        return (
            <>
                <div className="main_title">
                    <h1>Sub-Admin Detail</h1>
                    <p>
                        <button onClick={() => navigate("/dashboard")}>Dashboard</button>
                        {' '} - <button onClick={() => navigate("/manage-subadmin")}>Manage Sub-Admin</button>
                        {' '} - Sub-Admin Detail
                    </p>
                </div>
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                    <p>Sub-admin not found.</p>
                </Box>
            </>
        );
    }

    return (
        <>
            <div className="main_title">
                <h1>Sub-Admin Detail</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button>
                    {' '} - <button onClick={() => navigate("/manage-subadmin")}>Manage Sub-Admin</button>
                    {' '} - Sub-Admin Detail
                </p>
            </div>

            <div className="cards view_page">
                <div className="table_header">
                    <h2>Sub-Admin Details</h2>
                    <div className="right_s">
                        <Button
                            className="btn btn_primary sm"
                            onClick={() => navigate("/manage-subadmin/add", { state: { subAdminId: subAdmin._id } })}
                        >
                            Edit Sub-Admin
                        </Button>
                    </div>
                </div>

                <div className="gap_p">
                    <div className="vew_img w_100">
                        <h6>Image</h6>
                        <figure>
                            <Avatar
                                src={subAdmin.image || subAdmin.profile || "/static/images/user_placeholder.png"}
                                alt={subAdmin.fullName}
                                sx={{ width: 120, height: 120 }}
                            />
                        </figure>
                    </div>
                    <div className="w_33">
                        <h6>Name</h6>
                        <p>{subAdmin.fullName || "N/A"}</p>
                    </div>
                    <div className="w_33">
                        <h6>Email Id</h6>
                        <p>{subAdmin.email || "N/A"}</p>
                    </div>
                    <div className="w_33">
                        <h6>Phone Number</h6>
                        <p>
                            {subAdmin.countryCode || ""} {subAdmin.phone || "N/A"}
                        </p>
                    </div>
                    <div className="w_33">
                        <h6>Role</h6>
                        <p>
                            {(() => {
                                // Check permissionDetails first
                                if (subAdmin.permissionDetails?.name) {
                                    return subAdmin.permissionDetails.name;
                                }
                                // Check permissionId - can be object or string
                                const permissionId = subAdmin.permissionId as any;
                                if (permissionId && typeof permissionId === 'object' && permissionId.name) {
                                    return permissionId.name;
                                }
                                if (typeof permissionId === 'string') {
                                    return permissionId;
                                }
                                // Check permission - can be object or string
                                const permission = subAdmin.permission as any;
                                if (permission && typeof permission === 'object' && permission.name) {
                                    return permission.name;
                                }
                                if (typeof permission === 'string') {
                                    return permission;
                                }
                                return "N/A";
                            })()}
                        </p>
                    </div>
                    <div className="w_33">
                        <h6>Created Date</h6>
                        <p>{formatDate(subAdmin.createdAt)}</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SubadminDetail;
